import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Typography, keyframes } from '@mui/material'
import { styled } from '@mui/material/styles'
import { toast } from 'react-hot-toast'
import { useAddNoteTopic, useCreateTopic, useGetTopics, useUploadMedia } from '@/hooks/api/dashboard'
import { useLocalStorage } from '@/hooks/common'
import { OddlyouNoteType, PrivacyType, TopicType } from '@/utils/constants'
import { Topic } from '@/types'
import { topicCoverUrl, topicToAlbumSummary } from '@/features/album/mappers'
import { GlassPanel, STUDIO_ACCENT, monoLabelSx } from '../shared'

export interface SaveScreenshotSheetProps {
  open: boolean
  file: File | null
  galleryTitle?: string
  onClose: () => void
}

type SheetStep = 'pick' | 'create'

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const rise = keyframes`
  from { opacity: 0; transform: translateY(18px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`

const Scrim = styled(Box)({
  position: 'fixed',
  inset: 0,
  zIndex: 120,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  background: 'rgba(5,5,8,0.72)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  animation: `${fadeIn} 180ms ease`,
})

const Sheet = styled(GlassPanel)({
  width: 'min(560px, 100%)',
  maxHeight: 'min(860px, calc(100vh - 48px))',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 18,
  padding: 0,
  animation: `${rise} 280ms cubic-bezier(0.16,1,0.3,1)`,
  boxShadow: '0 28px 80px rgba(0,0,0,0.65), 0 0 0 1px rgba(255,255,255,0.04)',
})

const SpectrumEdge = styled(Box)({
  height: 2.5,
  background: 'var(--ds-gradient-spectrum)',
  flexShrink: 0,
})

const PreviewWrap = styled(Box)({
  position: 'relative',
  margin: '18px 18px 0',
  borderRadius: 14,
  overflow: 'hidden',
  aspectRatio: '16 / 10',
  background: '#050508',
  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)',
})

const RailShell = styled(Box)({
  position: 'relative',
})

const AlbumRail = styled(Box)({
  display: 'flex',
  gap: 10,
  overflowX: 'auto',
  padding: '4px 2px 6px',
  scrollSnapType: 'x proximity',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
})

const ArrowBtn = styled('button', { shouldForwardProp: (p) => p !== '$side' })<{ $side: 'left' | 'right' }>(
  ({ $side }) => ({
    all: 'unset',
    boxSizing: 'border-box',
    position: 'absolute',
    top: '50%',
    [$side === 'left' ? 'left' : 'right']: -2,
    transform: 'translateY(-58%)',
    zIndex: 2,
    width: 30,
    height: 30,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    color: 'var(--ds-night-ink)',
    background: 'rgba(19, 18, 25, 0.92)',
    border: '1px solid rgba(190,157,253,0.35)',
    boxShadow: '0 8px 20px rgba(0,0,0,0.45)',
    transition: 'background 140ms ease, border-color 140ms ease',
    '&:hover': {
      background: 'rgba(190,157,253,0.2)',
      borderColor: 'rgba(190,157,253,0.55)',
    },
    '&:disabled': {
      opacity: 0,
      pointerEvents: 'none',
    },
  }),
)

const EdgeFade = styled(Box, { shouldForwardProp: (p) => p !== '$side' })<{ $side: 'left' | 'right' }>(
  ({ $side }) => ({
    position: 'absolute',
    top: 0,
    bottom: 8,
    [$side]: 0,
    width: 36,
    zIndex: 1,
    pointerEvents: 'none',
    background:
      $side === 'left'
        ? 'linear-gradient(90deg, rgba(19,18,25,0.96), transparent)'
        : 'linear-gradient(270deg, rgba(19,18,25,0.96), transparent)',
  }),
)

const AlbumCard = styled('button', { shouldForwardProp: (p) => p !== '$on' })<{ $on?: boolean }>(({ $on }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  flex: '0 0 132px',
  scrollSnapAlign: 'start',
  cursor: 'pointer',
  borderRadius: 12,
  overflow: 'hidden',
  background: $on ? 'rgba(190,157,253,0.14)' : 'rgba(244,243,246,0.04)',
  border: `1px solid ${$on ? 'rgba(190,157,253,0.55)' : 'var(--ds-night-border)'}`,
  transition: 'border-color 140ms ease, transform 140ms ease, background 140ms ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    borderColor: $on ? 'rgba(190,157,253,0.7)' : 'rgba(244,243,246,0.22)',
  },
}))

const NameInput = styled('input')({
  width: '100%',
  height: 44,
  borderRadius: 12,
  border: '1px solid var(--ds-night-border)',
  background: 'rgba(244,243,246,0.05)',
  color: 'var(--ds-night-ink)',
  fontSize: 14,
  fontWeight: 550,
  padding: '0 14px',
  outline: 'none',
  fontFamily: 'inherit',
  transition: 'border-color 140ms ease, background 140ms ease',
  '&::placeholder': { color: 'var(--ds-night-ink-subtle)' },
  '&:focus': {
    borderColor: 'rgba(190,157,253,0.55)',
    background: 'rgba(190,157,253,0.08)',
  },
})

const PrimaryBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  position: 'relative',
  height: 40,
  padding: '0 20px',
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 650,
  cursor: 'pointer',
  color: '#fff',
  background: '#0d0d0f',
  '&:disabled': { opacity: 0.45, cursor: 'default' },
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: -2,
    borderRadius: 999,
    background: 'var(--ds-gradient-spectrum)',
    zIndex: -1,
  },
})

const GhostBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  height: 40,
  padding: '0 16px',
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  color: 'var(--ds-night-ink-muted)',
  border: '1px solid var(--ds-night-border)',
  background: 'rgba(244,243,246,0.05)',
  '&:hover': { color: 'var(--ds-night-ink)', background: 'rgba(244,243,246,0.1)' },
})

function stubAlbum(topicID: string, title: string): Topic {
  return {
    topicID,
    topicName: title,
    topicType: String(TopicType.ALBUM),
    photoCount: '0',
    mediaCount: 0,
    noteCount: '0',
    cliqueTypes: [],
    cliques: [],
    connections: [],
    dateAdded: '',
    dateCreated: '',
    dateLastInteracted: '',
    dateUpdated: '',
    latestEgo: undefined as unknown as Topic['latestEgo'],
    latestNote: undefined as unknown as Topic['latestNote'],
    firstNote: undefined as unknown as Topic['firstNote'],
    notes: [],
    ownerVisits: '0',
    photos: [],
    profilePicThumbURL: '',
    topicDescription: title,
    topicXData: '',
    totalComments: '0',
    totalDislikes: '0',
    totalLikes: '0',
    userID: '',
    userName: '',
    visibility: String(PrivacyType.PRIVATE),
    visitorVisits: '0',
  }
}

export function SaveScreenshotSheet({ open, file, galleryTitle, onClose }: SaveScreenshotSheetProps) {
  const [ewUser] = useLocalStorage('ewUser', null) as [{ userID?: string } | null, unknown]
  const getTopics = useGetTopics()
  const createTopic = useCreateTopic(ewUser?.userID || '')
  const uploadPhoto = useUploadMedia()
  const createTopicNote = useAddNoteTopic()

  const [step, setStep] = useState<SheetStep>('pick')
  const [albums, setAlbums] = useState<Topic[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [saving, setSaving] = useState(false)
  const [creating, setCreating] = useState(false)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const railRef = useRef<HTMLDivElement>(null)
  const nameInputRef = useRef<HTMLInputElement>(null)

  const syncRail = useCallback(() => {
    const el = railRef.current
    if (!el) {
      setCanScrollLeft(false)
      setCanScrollRight(false)
      return
    }
    const max = el.scrollWidth - el.clientWidth
    setCanScrollLeft(el.scrollLeft > 4)
    setCanScrollRight(max > 4 && el.scrollLeft < max - 4)
  }, [])

  const loadAlbums = useCallback(() => {
    getTopics.mutate(
      { pageNo: 1, interaction: Number(PrivacyType.PRIVATE), topicType: Number(TopicType.ALBUM) },
      {
        onSuccess: (data) => {
          setAlbums((data || []) as Topic[])
          requestAnimationFrame(syncRail)
        },
      },
    )
  }, [getTopics, syncRail])

  useEffect(() => {
    if (!open) return
    setStep('pick')
    setSelectedId(null)
    setNewName('')
    setCreating(false)
    loadAlbums()
  }, [open])

  useEffect(() => {
    if (step === 'create') {
      const t = window.setTimeout(() => nameInputRef.current?.focus(), 40)
      return () => window.clearTimeout(t)
    }
  }, [step])

  useEffect(() => {
    const el = railRef.current
    if (!el) return
    syncRail()
    el.addEventListener('scroll', syncRail, { passive: true })
    window.addEventListener('resize', syncRail)
    return () => {
      el.removeEventListener('scroll', syncRail)
      window.removeEventListener('resize', syncRail)
    }
  }, [albums, step, syncRail])

  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file])
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  if (!open) return null

  const scrollRail = (dir: -1 | 1) => {
    const el = railRef.current
    if (!el) return
    el.scrollBy({ left: dir * Math.max(220, el.clientWidth * 0.7), behavior: 'smooth' })
  }

  const createAlbum = async () => {
    const title = newName.trim()
    if (!title || creating) return
    if (!ewUser?.userID) {
      toast.error('Sign in to create an album')
      return
    }
    setCreating(true)
    try {
      const result = await createTopic.mutateAsync({
        topicName: title,
        topicType: TopicType.ALBUM.toString(),
        allowContribution: 0,
        topicXData: 'X Data Here',
        privacyType: PrivacyType.PRIVATE,
        cliques: [],
        connections: [],
      })
      if (result?.isError) throw new Error(result.errorMessage || 'Could not create album')
      const topicID = String(result.topicID)
      const created = stubAlbum(topicID, title)
      setAlbums((prev) => [created, ...prev.filter((a) => a.topicID !== topicID)])
      setSelectedId(topicID)
      setNewName('')
      setStep('pick')
      toast.success('Album ready — save your shot', { duration: 2200 })
      requestAnimationFrame(() => {
        railRef.current?.scrollTo({ left: 0, behavior: 'smooth' })
        syncRail()
      })
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Could not create album')
    } finally {
      setCreating(false)
    }
  }

  const submit = async () => {
    if (!file || !selectedId) return
    setSaving(true)
    try {
      const fileInfo = await uploadPhoto.mutateAsync(file)
      await createTopicNote.mutateAsync({
        noteName: 'Screenshot',
        topicID: selectedId,
        noteText: JSON.stringify({
          text: `<p>Studio screenshot${galleryTitle ? ` · ${galleryTitle}` : ''}</p>`,
          attachments: [],
        }),
        egoItems: [
          {
            sequenceID: '1',
            egoID: fileInfo.egoID,
            photoAlbumURL: fileInfo.egoAlbumURL,
            photoFullURL: fileInfo.egoImageURL,
          },
        ],
        noteType: OddlyouNoteType.IMAGE.toString(),
      })
      toast.success('Saved to album', { icon: '📷', duration: 2800 })
      onClose()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Could not save screenshot')
    } finally {
      setSaving(false)
    }
  }

  const showArrows = canScrollLeft || canScrollRight

  return (
    <Scrim onClick={onClose} role='presentation'>
      <Sheet
        onClick={(e) => e.stopPropagation()}
        role='dialog'
        aria-modal
        aria-label='Save screenshot to an album'
      >
        <SpectrumEdge />
        <Box sx={{ px: '20px', pt: '16px', pb: '6px', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography sx={{ ...monoLabelSx, color: STUDIO_ACCENT.gold, mb: '6px' }}>
              {step === 'create' ? 'New album' : 'Capture'}
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 720, letterSpacing: '-0.02em' }}>
              {step === 'create' ? 'Name this album' : 'Save this view'}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: 'var(--ds-night-ink-muted)', mt: '4px' }}>
              {step === 'create'
                ? 'Stays in this sheet — then drop your screenshot into it.'
                : 'Pick an album for this frame, or make a new one here.'}
            </Typography>
          </Box>
          <GhostBtn
            type='button'
            onClick={onClose}
            aria-label='Close'
            style={{ height: 32, width: 32, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            ✕
          </GhostBtn>
        </Box>

        <PreviewWrap>
          {previewUrl ? (
            <Box
              component='img'
              src={previewUrl}
              alt='Studio screenshot'
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Box sx={{ ...monoLabelSx, height: '100%', display: 'grid', placeItems: 'center' }}>Developing…</Box>
          )}
        </PreviewWrap>

        {step === 'pick' ? (
          <>
            <Box sx={{ px: '20px', pt: '18px', pb: '8px' }}>
              <Box display='flex' alignItems='center' justifyContent='space-between' mb='10px'>
                <Typography sx={monoLabelSx}>Choose album</Typography>
                {showArrows && (
                  <Typography sx={{ ...monoLabelSx, color: 'var(--ds-night-ink-subtle)' }}>Scroll →</Typography>
                )}
              </Box>
              {getTopics.isPending && albums.length === 0 && (
                <Typography sx={{ fontSize: 13, color: 'var(--ds-night-ink-subtle)', py: 2 }}>Loading albums…</Typography>
              )}
              {!getTopics.isPending && albums.length === 0 && (
                <Typography sx={{ fontSize: 13, color: 'var(--ds-night-ink-subtle)', py: 1.5 }}>
                  No albums yet — create one below to keep this shot.
                </Typography>
              )}
              {albums.length > 0 && (
                <RailShell>
                  {canScrollLeft && <EdgeFade $side='left' />}
                  {canScrollRight && <EdgeFade $side='right' />}
                  <ArrowBtn type='button' $side='left' disabled={!canScrollLeft} onClick={() => scrollRail(-1)} aria-label='Previous albums'>
                    ‹
                  </ArrowBtn>
                  <ArrowBtn type='button' $side='right' disabled={!canScrollRight} onClick={() => scrollRail(1)} aria-label='More albums'>
                    ›
                  </ArrowBtn>
                  <AlbumRail ref={railRef}>
                    {albums.map((album) => {
                      const summary = topicToAlbumSummary(album)
                      const cover = topicCoverUrl(album)
                      const on = album.topicID === selectedId
                      return (
                        <AlbumCard key={album.topicID} type='button' $on={on} onClick={() => setSelectedId(album.topicID)}>
                          <Box
                            sx={{
                              aspectRatio: '4/3',
                              background: cover
                                ? `center/cover url(${cover})`
                                : `linear-gradient(145deg, rgba(190,157,253,0.25), rgba(1,218,254,0.12)), #17161d`,
                            }}
                          />
                          <Box sx={{ px: '10px', py: '9px' }}>
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: on ? 680 : 600,
                                color: 'var(--ds-night-ink)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {summary.title}
                            </Typography>
                            <Typography sx={{ ...monoLabelSx, mt: '3px', fontSize: 7 }}>
                              {summary.photoCount} photos
                            </Typography>
                          </Box>
                        </AlbumCard>
                      )
                    })}
                  </AlbumRail>
                </RailShell>
              )}
            </Box>

            <Box
              sx={{
                px: '20px',
                pt: '8px',
                pb: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                flexWrap: 'wrap',
                borderTop: '1px solid var(--ds-night-border)',
                mt: '4px',
              }}
            >
              <GhostBtn type='button' onClick={() => setStep('create')}>
                ＋ New album
              </GhostBtn>
              <Box display='flex' gap={1.25}>
                <GhostBtn type='button' onClick={onClose}>
                  Cancel
                </GhostBtn>
                <PrimaryBtn type='button' onClick={submit} disabled={!selectedId || !file || saving}>
                  {saving ? 'Saving…' : 'Save to album'}
                </PrimaryBtn>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ px: '20px', pt: '18px', pb: '8px' }}>
              <Typography sx={{ ...monoLabelSx, mb: '10px' }}>Album name</Typography>
              <NameInput
                ref={nameInputRef}
                value={newName}
                placeholder='e.g. Studio captures'
                maxLength={80}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    void createAlbum()
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault()
                    setStep('pick')
                  }
                }}
              />
              <Typography sx={{ fontSize: 12, color: 'var(--ds-night-ink-subtle)', mt: '10px', lineHeight: 1.4 }}>
                Creates a private album, selects it, then you’re back here to save the screenshot.
              </Typography>
            </Box>

            <Box
              sx={{
                px: '20px',
                pt: '8px',
                pb: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                flexWrap: 'wrap',
                borderTop: '1px solid var(--ds-night-border)',
                mt: '4px',
              }}
            >
              <GhostBtn
                type='button'
                onClick={() => {
                  setStep('pick')
                  setNewName('')
                }}
              >
                ← Back
              </GhostBtn>
              <PrimaryBtn type='button' onClick={() => void createAlbum()} disabled={!newName.trim() || creating}>
                {creating ? 'Creating…' : 'Create & continue'}
              </PrimaryBtn>
            </Box>
          </>
        )}
      </Sheet>
    </Scrim>
  )
}
